import React, { useCallback, useEffect, useState } from "react";
import { Page, BlockStack } from "@shopify/polaris";
import criticalImage from "images/home/critical.svg";
import oosImage from "images/home/out-of-stock.svg";
import importingInProgressImage from "images/home/importing_shopify_store_no_bg.png";
import oossImage from "images/home/out-of-stock-soon.svg";
import osImage from "images/home/overstock.svg";
import ReviewBlock from "./support/ReviewBlock";
import Onboarding from "./support/Onboarding";
import OnboardingRecommendations from "./support/OnboardingRecommendations";
import ImportProgressBanner from "./support/ImportProgressBanner";
import {
  getHomeRecommendations,
  useHorseLocationsOptions,
  useShopifyChannelSalesOptions,
  useShopifyShop,
} from "../../api_utils/requests";
import { Footer } from "../common/Footer";
import type { HomeQueryParams, HomeRecommendationResponse, HomeRecommendationsResponse } from "../../api_utils/types";
import type { NonEmptyArray } from "@shopify/polaris/build/ts/src/types";
import type { IndexTableHeading } from "@shopify/polaris/build/ts/src/components/IndexTable";
import { HomeIndexPageSkeleton } from "../common/skeletons";
import RecommendationCard from "./support/RecommendationCard";
import EmptyRecommendationCard from "./support/EmptyRecommendationCard";

const importingStockoutsEmptyState = (
  <EmptyRecommendationCard
    header="Your information is still being imported"
    imageSrc={importingInProgressImage}
    infoMessage="We'll have advice about your stockouts soon."
    title="Stockouts"
  />
);

const importingOutOfStockSoonEmptyState = (
  <EmptyRecommendationCard
    header="Your information is still being imported"
    imageSrc={importingInProgressImage}
    infoMessage="Your forecast about which products will be out of stock soon is being calculated."
    title="Out of stock soon"
  />
);

const importingOverstockEmptyState = (
  <EmptyRecommendationCard
    header="Your information is still being imported"
    imageSrc={importingInProgressImage}
    infoMessage="Hang tight while we figure out which variants you have too many of."
    title="Overstock"
  />
);

const importingBelowMinEmptyState = (
  <EmptyRecommendationCard
    header="Your information is still being imported"
    imageSrc={importingInProgressImage}
    infoMessage="We're calculating which variants are below your minimums."
    seeMoreUrl="/horse_variants"
    seeMoreUrlText="Configure minimums"
    title="Below minimums"
  />
);

const oosEmptyState = (
  <EmptyRecommendationCard
    header="All of your variants are in-stock"
    imageSrc={oosImage}
    infoMessage="You haven’t lost any money due to lost sales."
    seeMoreUrl="/sales_trends?info_query=out_of_stock"
    title="Stockouts"
  />
);
const oossEmptyState = (
  <EmptyRecommendationCard
    header="You're well stocked"
    imageSrc={oossImage}
    infoMessage="None of your variants will sell out in the next 180 days."
    seeMoreUrl="/sales_trends?info_query=out_of_stock_quite_soon"
    title="Out of stock soon"
  />
);
const osEmptyState = (
  <EmptyRecommendationCard
    header="You didn’t waste your capital"
    imageSrc={osImage}
    infoMessage="You’re being efficient with your capital and didn’t buy too much."
    seeMoreUrl="/sales_trends?info_query=overstock"
    title="Overstock"
  />
);

const belowMinEmptyState = (
  <EmptyRecommendationCard
    header="You're well stocked"
    imageSrc={oossImage}
    infoMessage="None of your variants are below your minimum target quantities."
    seeMoreUrl="/horse_variants"
    seeMoreUrlText="Configure minimums"
    title="Minimums"
  />
);

const outOfStockColumnNames: NonEmptyArray<IndexTableHeading> = [
  { title: "Variant" },
  { title: "Sold out date" },
  { title: "90 day lost sales" },
];
const oossFutureLostColumnNames: NonEmptyArray<IndexTableHeading> = [
  { title: "Variant" },
  { title: "Available" },
  { title: "Daily sales" },
];
const overStockColumnNames: NonEmptyArray<IndexTableHeading> = [
  { title: "Variant" },
  { title: "Available" },
  { title: "Frozen capital" },
];
const belowMinColumnNames: NonEmptyArray<IndexTableHeading> = [
  { title: "Variant" },
  { title: "Available" },
  { title: "Minimum needed" },
];
const helpCenterUrl = "https://horse-inventory.notion.site/Help-Center-5ade9ae7bcd2430faf8945369b9af98f";

function HomeIndex(): React.ReactElement {
  const [outOfStockSoonActive, setOutOfStockSoonActive] = useState(false);
  const [outOfStockActive, setOutOfStockActive] = useState(false);
  const [overStockActive, setOverStockActive] = useState(false);
  const [belowMinActive, setBelowMinActive] = useState(false);
  
  const [outOfStockSoonRows, setOutOfStockSoonRows] = useState<HomeRecommendationResponse[]>([]);
  const [outOfStockRows, setOutOfStockRows] = useState<HomeRecommendationResponse[]>([]);
  const [overStockRows, setOverStockRows] = useState<HomeRecommendationResponse[]>([]);
  const [belowMinRows, setBelowMinRows] = useState<HomeRecommendationResponse[]>([]);
  
  const [outOfStockSoonTotalFutureLost, setOutOfStockSoonTotalFutureLost] = useState(0);
  const [outOfStockTotalLost, setOutOfStockTotalLost] = useState(0);
  const [overStockTotalFrozen, setOverStockTotalFrozen] = useState(0);
  const [belowMinTotal, setBelowMinTotal] = useState(0);

  const [query, setQuery] = useState<HomeQueryParams>({ horse_location_id: [], sale_shopify_channel_ids: [] });
  const [isLoading, setIsLoading] = useState(false);

  const { data: horseLocations } = useHorseLocationsOptions({ omitAll: true });
  const { data: shopifyChannels, isLoading: pageLoading } = useShopifyChannelSalesOptions();

  const setRowsAndTotalValue = useCallback((apiResp: HomeRecommendationsResponse): void => {
    setOutOfStockSoonActive(outOfStockSoonActive || apiResp.out_of_stock_soon.length > 0);
    setOutOfStockActive(outOfStockActive || apiResp.out_of_stock.length > 0);
    setOverStockActive(overStockActive || apiResp.over_stock.length > 0);
    setBelowMinActive(belowMinActive || apiResp.below_min.length > 0);

    setOutOfStockSoonRows(apiResp.out_of_stock_soon);
    setOutOfStockRows(apiResp.out_of_stock);
    setOverStockRows(apiResp.over_stock);
    setBelowMinRows(apiResp.below_min);

    setOutOfStockTotalLost(apiResp.oos_lost);
    setOutOfStockSoonTotalFutureLost(apiResp.ooss_future_lost);
    setOverStockTotalFrozen(apiResp.os_frozen);
    setBelowMinTotal(apiResp.below_min.reduce((acc, home_recommendation) => {
      return acc + (home_recommendation.min - home_recommendation.available);
    }, 0));
  }, [
    setOutOfStockSoonActive,
    setOutOfStockActive,
    setOverStockActive,
    setBelowMinActive,
    setOutOfStockSoonRows,
    setOutOfStockRows,
    setOverStockRows,
    setBelowMinRows,
    setOutOfStockTotalLost,
    setOutOfStockSoonTotalFutureLost,
    setOverStockTotalFrozen,
    setBelowMinTotal,
    outOfStockSoonActive,
    outOfStockActive,
    overStockActive,
    belowMinActive,
  ]);

  const handleSelectChange =
    (name: keyof HomeQueryParams) =>
    (val: HomeQueryParams[keyof HomeQueryParams]): void => {
      setQuery({ ...query, [name]: val });
    };

  useEffect(() => {
    setIsLoading(true);
    void getHomeRecommendations(query).then((res) => {
      setRowsAndTotalValue(res);
      setIsLoading(false);
    });
  }, [query, setRowsAndTotalValue]);

  const handleLocationSelectRemove = (horse_location_id: number): void => {
    const left = query.horse_location_id.filter((select) => select !== horse_location_id);
    setQuery({ ...query, horse_location_id: left });
  };

  const handleShopifyChannelSelectRemove = (sale_shopify_channel_id: number): void => {
    const left = query.sale_shopify_channel_ids.filter((select) => select !== sale_shopify_channel_id);
    setQuery({ ...query, sale_shopify_channel_ids: left });
  };

  const { data: shopifyShop, isFetched: isShopifyShopFetched, isLoading: isShopifyShopLoading } = useShopifyShop();
  const { readyForRecommendations } = shopifyShop;

  const renderOutOfStock = (): React.ReactElement => {
    if (!readyForRecommendations) {
      return importingStockoutsEmptyState;
    } else if (outOfStockActive || isLoading) {
      return (
        <RecommendationCard
          amount={outOfStockTotalLost}
          buttonUrl="/purchase_orders/new"
          columnNames={outOfStockColumnNames}
          handleLocationSelectRemove={handleLocationSelectRemove}
          handleSelectLocationChange={handleSelectChange("horse_location_id")}
          handleSelectShopifyChannelChange={handleSelectChange("sale_shopify_channel_ids")}
          handleShopifyChannelSelectRemove={handleShopifyChannelSelectRemove}
          header="Stockouts"
          horseLocations={horseLocations}
          horse_location_id={query.horse_location_id}
          imageSrc={criticalImage}
          info_one="These variants are out of stock. Consider ordering more so that you don’t lose any more sales."
          info_three="in sales due to out-of-stock variants!"
          info_two="You have lost an estimated"
          isLoading={isLoading}
          position={0}
          sale_shopify_channel_ids={query.sale_shopify_channel_ids}
          shopifyChannels={shopifyChannels}
          showPerDay={false}
          tableRows={outOfStockRows}
          url="/sales_trends?info_query=out_of_stock"
        />
      );
    } else {
      return oosEmptyState;
    }
  };

  const renderOutOfStockSoonRender = (): React.ReactElement => {
    if (!readyForRecommendations) {
      return importingOutOfStockSoonEmptyState;
    } else if (outOfStockSoonActive || isLoading) {
      return (
        <RecommendationCard
          amount={outOfStockSoonTotalFutureLost}
          buttonUrl="/purchase_orders/new"
          columnNames={oossFutureLostColumnNames}
          handleLocationSelectRemove={handleLocationSelectRemove}
          handleSelectLocationChange={handleSelectChange("horse_location_id")}
          handleSelectShopifyChannelChange={handleSelectChange("sale_shopify_channel_ids")}
          handleShopifyChannelSelectRemove={handleShopifyChannelSelectRemove}
          header="Out of stock soon"
          horseLocations={horseLocations}
          horse_location_id={query.horse_location_id}
          imageSrc={criticalImage}
          info_one="These variants will run out of stock soon. Consider ordering more so that you don't lose any sales."
          info_three="if these variants go out-of-stock!"
          info_two="You could lose"
          isLoading={isLoading}
          position={1}
          sale_shopify_channel_ids={query.sale_shopify_channel_ids}
          shopifyChannels={shopifyChannels}
          showPerDay
          tableRows={outOfStockSoonRows}
          url="/sales_trends?info_query=out_of_stock_quite_soon"
        />
      );
    } else {
      return oossEmptyState;
    }
  };

  const renderOverStockRender = (): React.ReactElement => {
    if (!readyForRecommendations) {
      return importingOverstockEmptyState;
    } else if (overStockActive || isLoading) {
      return (
        <RecommendationCard
          amount={overStockTotalFrozen}
          columnNames={overStockColumnNames}
          handleLocationSelectRemove={handleLocationSelectRemove}
          handleSelectLocationChange={handleSelectChange("horse_location_id")}
          handleSelectShopifyChannelChange={handleSelectChange("sale_shopify_channel_ids")}
          handleShopifyChannelSelectRemove={handleShopifyChannelSelectRemove}
          header="Overstock"
          horseLocations={horseLocations}
          horse_location_id={query.horse_location_id}
          imageSrc={criticalImage}
          info_one="These variants aren’t selling very well.Unlock your frozen capital by discounting them so that they sell faster."
          info_three="in frozen capital."
          info_two="You have"
          isLoading={isLoading}
          position={2}
          sale_shopify_channel_ids={query.sale_shopify_channel_ids}
          shopifyChannels={shopifyChannels}
          showPerDay={false}
          tableRows={overStockRows}
          url="/sales_trends?info_query=overstock"
        />
      );
    } else {
      return osEmptyState;
    }
  };

  const renderBelowMinRender = (): React.ReactElement => {
    if (!readyForRecommendations) {
      return importingBelowMinEmptyState;
    } else if (belowMinActive || isLoading) {
      return <RecommendationCard
        amount={belowMinTotal}
        columnNames={belowMinColumnNames}
        handleLocationSelectRemove={handleLocationSelectRemove}
        handleSelectLocationChange={handleSelectChange("horse_location_id")}
        header="Below minimums"
        hideWarning
        horseLocations={horseLocations}
        horse_location_id={query.horse_location_id}
        imageSrc={criticalImage}
        info_one="These variants are below your set minimums. Consider ordering more so that you don't lose any sales."
        info_three="if these variants go below your set minimums."
        info_two="You have"
        isLoading={isLoading}
        position={2}
        showPerDay={false}
        tableRows={belowMinRows}
        url="/sales_trends?info_query=below_min"
      />
    } else {
      return belowMinEmptyState;
    }
  };

  const pageMarkup = (
    <Page narrowWidth>
      <Onboarding />
      <OnboardingRecommendations readyForRecommendations={readyForRecommendations ? isShopifyShopFetched : null} />
      <ImportProgressBanner />
      <BlockStack gap="400">
        {renderOutOfStock()}
        {renderOutOfStockSoonRender()}
        {renderOverStockRender()}
        {renderBelowMinRender()}
        <ReviewBlock />
      </BlockStack>
      <Footer pageTitle="Get help with Horse" url={helpCenterUrl} />
    </Page>
  );

  return (pageLoading || isShopifyShopLoading) ? <HomeIndexPageSkeleton /> : pageMarkup;
}

export default HomeIndex;
